import React, { useState, useEffect, useRef } from 'react';
import { Home, User, MapPin, Camera, CheckCircle, XCircle, ArrowLeft, Check, Loader2, ZoomIn, RefreshCw, LogOut, Lock, Trash2, Package, CalendarDays } from 'lucide-react';
import liff from '@line/liff';
import imageCompression from 'browser-image-compression';
import InventoryScreen from './InventoryScreen';
import WorkPlanScreen from './WorkPlanScreen';

import { login, logout, getTasks, getTask, toggleItem, uploadPhoto, deletePhoto, submitTask, getUser, changePassword, checkLineAuth, registerLineUser, checkDingTalkAuth, confirmDingTalkAuth, ASSET_BASE_URL } from './api';

import RegisterScreen from './RegisterScreen';

// --- 主应用组件 ---
import { useLanguage } from './LanguageContext';

const DINGTALK_SESSION_STORAGE_KEY = 'homestay_clean_dingtalk_session';

const dingTalkCorpIdFromQuery = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('corpid')
    || params.get('amp;corpid')
    || params.get('corpId')
    || params.get('amp;corpId')
    || params.get('corpID')
    || params.get('amp;corpID')
    || '';
};

const isDingTalkClient = () => /DingTalk|AliApp\(DingTalk/i.test(window.navigator.userAgent || '');

const isDingTalkEntry = () => {
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname.replace(/\/+$/, '') === '/dingtalk'
    || params.get('entry') === 'dingtalk'
    || Boolean(dingTalkCorpIdFromQuery());
};

const dingTalkCorpId = () => {
  return dingTalkCorpIdFromQuery()
    || process.env.REACT_APP_DINGTALK_CORP_ID
    || '';
};

const requestDingTalkAuthCode = () => new Promise((resolve, reject) => {
  const dd = window.dd;

  if (!dd) {
    reject(new Error('钉钉 JSAPI 未加载，请从钉钉酷应用打开。'));
    return;
  }

  const finish = (result) => {
    const code = result?.code || result?.authCode;

    if (code) {
      resolve(code);
    } else {
      reject(new Error('无法取得钉钉免登码，请重新打开酷应用。'));
    }
  };

  const fail = (error) => reject(new Error(error?.errorMessage || error?.message || '钉钉免登失败，请重新打开酷应用。'));

  const run = () => {
    const corpId = dingTalkCorpId();
    const options = {
      corpId,
      success: finish,
      fail,
      onSuccess: finish,
      onFail: fail,
    };

    try {
      const requestAuthCode = dd.runtime?.permission?.requestAuthCode || dd.getAuthCode;
      const maybePromise = requestAuthCode?.call(dd.runtime?.permission || dd, options);

      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(finish).catch(fail);
      } else if (!requestAuthCode) {
        fail(new Error('当前钉钉环境不支持免登。'));
      }
    } catch (error) {
      fail(error);
    }
  };

  if (typeof dd.ready === 'function') {
    dd.ready(run);
  } else {
    run();
  }
});

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [page, setPage] = useState('tasks');
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // Add loading state for auth check
  const [lineIdToken, setLineIdToken] = useState(null); // Store Line Token
  const [lineNoticeKey, setLineNoticeKey] = useState('');
  const [lineRegistrationRequired, setLineRegistrationRequired] = useState(false);
  const [isLineSession, setIsLineSession] = useState(false);
  const [isDingTalkSession, setIsDingTalkSession] = useState(() => (
    localStorage.getItem(DINGTALK_SESSION_STORAGE_KEY) === '1' || isDingTalkClient()
  ));
  const [isCompletingLineLogin, setIsCompletingLineLogin] = useState(false);
  const [dingTalkConfirmData, setDingTalkConfirmData] = useState(null);
  const [dingTalkNotice, setDingTalkNotice] = useState('');
  const [isCompletingDingTalkLogin, setIsCompletingDingTalkLogin] = useState(false);

  useEffect(() => {
    const initApp = async () => {
      if (isDingTalkClient()) {
        localStorage.setItem(DINGTALK_SESSION_STORAGE_KEY, '1');
        setIsDingTalkSession(true);
        setIsLineSession(false);
      }

      if (isDingTalkEntry()) {
        localStorage.removeItem('token');
        localStorage.setItem(DINGTALK_SESSION_STORAGE_KEY, '1');
        setIsDingTalkSession(true);
        setIsLineSession(false);

        try {
          const authCode = await requestDingTalkAuthCode();
          const res = await checkDingTalkAuth(authCode);

          if (res.data?.access_token) {
            localStorage.setItem('token', res.data.access_token);
            setUser(res.data.user);
            setIsLoggedIn(true);
            fetchUser();
            fetchTasks();
            setIsCheckingAuth(false);
            return;
          }

          if (res.data?.status === 'needs_confirm') {
            setDingTalkConfirmData({
              name: res.data.name,
              confirmToken: res.data.confirm_token,
            });
            setIsCheckingAuth(false);
            return;
          }
        } catch (err) {
          const payload = err.response?.data;
          setDingTalkNotice(payload?.message || err.message || '钉钉免登录失败，请重新打开酷应用。');
          setIsCheckingAuth(false);
          return;
        }

        setIsCheckingAuth(false);
        return;
      }

      // LIFF Initialization (skip if not configured)
      const liffId = process.env.REACT_APP_LIFF_ID;
      const isLineEntry = /Line\//i.test(window.navigator.userAgent)
        || window.location.search.includes('liff.state')
        || window.location.hash.includes('liff.state');

      if (liffId && liffId !== 'your_frontend_liff_id' && isLineEntry) {
        setIsLineSession(true);
        setIsDingTalkSession(false);
        localStorage.removeItem('token');
        localStorage.removeItem(DINGTALK_SESSION_STORAGE_KEY);

        try {
          await liff.init({ liffId });

          if (!liff.isLoggedIn()) {
            liff.login({ redirectUri: window.location.href });
            return;
          }

          if (liff.isLoggedIn()) {
            const idToken = liff.getIDToken();
            setLineIdToken(idToken);

            if (idToken) {
              try {
                const res = await checkLineAuth(idToken);
                if (res.status === 200) {
                  localStorage.setItem('token', res.data.access_token);
                  setUser(res.data.user);
                  setIsLineSession(true);
                  setIsLoggedIn(true);
                  fetchUser();
                  fetchTasks();
                  setIsCheckingAuth(false);
                  return;
                }
              } catch (err) {
                const status = err.response?.status;

                if (status === 404) {
                  setLineRegistrationRequired(true);
                  setIsCheckingAuth(false);
                  return;
                }

                if (status === 403) {
                  setLineNoticeKey('lineNotice.pendingActivation');
                } else {
                  setLineNoticeKey('lineNotice.autoLoginFailed');
                }

                setIsCheckingAuth(false);
                return;
              }
            } else {
              setLineNoticeKey('lineNotice.missingLineInfo');
              setIsCheckingAuth(false);
              return;
            }
          }
        } catch (err) {
          console.error("LIFF Init Failed", err);
          setLineNoticeKey('lineNotice.autoLoginFailed');
          setIsCheckingAuth(false);
          return;
        }

        setIsCheckingAuth(false);
        return;
      }

      // Standard Token Check (Existing Logic)
      const token = localStorage.getItem('token');
      if (token) {
        // Validate token endpoint? Current app relies on fetchUser?
        // Existing logic:
        setIsLoggedIn(true);
        fetchUser();
        fetchTasks();
      }

      setIsCheckingAuth(false); // Finished checking
    };

    initApp();
  }, []);

  const handleDingTalkConfirm = async () => {
    if (!dingTalkConfirmData?.confirmToken) return;

    setIsCompletingDingTalkLogin(true);

    try {
      const res = await confirmDingTalkAuth(dingTalkConfirmData.confirmToken);

      if (res.data?.access_token) {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem(DINGTALK_SESSION_STORAGE_KEY, '1');
        setUser(res.data.user);
        setIsLoggedIn(true);
        setIsDingTalkSession(true);
        setIsLineSession(false);
        setPage('tasks');
        setDingTalkConfirmData(null);
        setDingTalkNotice('');
        fetchUser();
        fetchTasks();
        return;
      }

      if (res.data?.status === 'pending_activation') {
        setDingTalkConfirmData(null);
        setDingTalkNotice(res.data.message || '绑定成功，请等待管理员开启登录。');
      }
    } catch (error) {
      const payload = error.response?.data;
      setDingTalkNotice(payload?.message || error.message || '钉钉绑定失败，请联系管理员。');
      setDingTalkConfirmData(null);
    } finally {
      setIsCompletingDingTalkLogin(false);
    }
  };

  const handleLineRegistrationSuccess = async (data) => {
    if (data?.status === 'active' && lineIdToken) {
      setIsCompletingLineLogin(true);

      try {
        const res = await checkLineAuth(lineIdToken);
        localStorage.setItem('token', res.data.access_token);
        localStorage.removeItem(DINGTALK_SESSION_STORAGE_KEY);
        setUser(res.data.user);
        setIsLineSession(true);
        setIsDingTalkSession(false);
        setIsLoggedIn(true);
        setPage('tasks');
        setLineRegistrationRequired(false);
        setLineNoticeKey('');
        fetchUser();
        fetchTasks();
        return;
      } catch (error) {
        console.error("LINE auto-login after registration failed", error);
        setLineRegistrationRequired(false);
        setLineNoticeKey('lineNotice.autoLoginAfterBindFailed');
        return;
      } finally {
        setIsCompletingLineLogin(false);
      }
    }

    setLineRegistrationRequired(false);
    setLineNoticeKey(data?.status === 'pending_activation' ? 'lineNotice.pendingActivation' : 'lineNotice.pendingRegistration');
  };

  const fetchUser = async () => {
    try {
      const res = await getUser();
      setUser(res.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      // Handle invalid/expired token (401 or 403)
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        handleLogout();
      }
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await getTasks();
      const sortedTasks = res.data.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
      setTasks(sortedTasks);
    } catch (error) {
      console.error("Failed to fetch tasks", error);
    }
  };



  const navigateTo = (pageName) => {
    setIsLoading(true);
    setTimeout(() => {
      setPage(pageName);
      setIsLoading(false);
    }, 300);
  };

  const handleSelectTask = async (task) => {
    // Check if task is scheduled for the future
    const scheduledTime = new Date(task.scheduled_time);
    const now = new Date();
    if (scheduledTime > now) {
      // alert(t('taskDetail.tooEarly')); // You might need to add this translation key or use a hardcoded string for now
      // return;
    }

    setIsLoading(true);
    try {
      const res = await getTask(task.id);
      setSelectedTask(res.data);
      navigateTo('taskDetail');
    } catch (error) {
      console.error("Failed to fetch task details", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTask = (updatedTask) => {
    const newTasks = tasks.map(t => t.id === updatedTask.id ? updatedTask : t);
    setTasks(newTasks);
    setSelectedTask(updatedTask);
  };

  const handleSubmitReview = async (task, remarks) => {
    try {
      await submitTask(task.id, remarks);
      fetchTasks(); // Refresh list
      navigateTo('tasks');
    } catch (error) {
      console.error("Failed to submit task", error);
    }
  };

  const handleLogin = async (email, password) => {
    try {
      // Pass lineIdToken if available for binding
      const res = await login(email, password, lineIdToken);
      localStorage.setItem('token', res.data.access_token);
      const nextIsDingTalkSession = isDingTalkClient();
      if (nextIsDingTalkSession) {
        localStorage.setItem(DINGTALK_SESSION_STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(DINGTALK_SESSION_STORAGE_KEY);
      }
      setUser(res.data.user);
      setIsDingTalkSession(nextIsDingTalkSession);
      if (lineIdToken) {
        setIsLineSession(true);
      }

      // Delay to show success message
      await new Promise(resolve => setTimeout(resolve, 1000));

      setIsLoggedIn(true);
      fetchTasks();
    } catch (error) {
      console.error("Login failed", error);
      throw error; // Re-throw to let LoginScreen handle it
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem(DINGTALK_SESSION_STORAGE_KEY);
      setIsLoggedIn(false);
      setIsLineSession(false);
      setIsDingTalkSession(false);
      setPage('tasks');
      setUser(null);
    }
  };

  const renderPage = () => {
    if (isLoading) {
      return <div className="flex justify-center items-center h-screen bg-gray-50"><Loader2 className="animate-spin text-indigo-500" size={48} /></div>;
    }

    if (page === 'register') {
      return <RegisterScreen onBack={() => setPage('login')} onRegisterSuccess={() => setPage('login')} />;
    }

    if (isCheckingAuth || isCompletingLineLogin || isCompletingDingTalkLogin) {
      return <div className="flex justify-center items-center h-screen bg-gray-50"><Loader2 className="animate-spin text-indigo-500" size={48} /></div>;
    }

    if (dingTalkNotice) {
      return <DingTalkNoticeScreen message={dingTalkNotice} />;
    }

    if (dingTalkConfirmData) {
      return <DingTalkConfirmScreen name={dingTalkConfirmData.name} onConfirm={handleDingTalkConfirm} />;
    }

    if (lineNoticeKey) {
      return <LineNoticeScreen messageKey={lineNoticeKey} />;
    }

    if (lineRegistrationRequired) {
      return <LineRegistrationScreen lineIdToken={lineIdToken} onRegistered={handleLineRegistrationSuccess} />;
    }

    if (!isLoggedIn) {
      return <LoginScreen onLogin={handleLogin} onGoToRegister={() => setPage('register')} />;
    }

    switch (page) {
      case 'tasks':
        return <TaskListScreen tasks={tasks} onSelectTask={handleSelectTask} onGoToProfile={() => navigateTo('profile')} onGoToWorkPlan={() => navigateTo('workPlan')} onRefresh={fetchTasks} />;
      case 'taskDetail':
        return <TaskDetailScreen task={selectedTask} onUpdateTask={handleUpdateTask} onSubmit={handleSubmitReview} onBack={() => navigateTo('tasks')} />;

      case 'profile':
        return <ProfileScreen user={user} isLineSession={isLineSession} isDingTalkSession={isDingTalkSession} onGoToTasks={() => navigateTo('tasks')} onLogout={handleLogout} />;
      case 'inventory':
        return <InventoryScreen onGoToProfile={() => navigateTo('profile')} />;
      case 'workPlan':
        return <WorkPlanScreen onBack={() => navigateTo('tasks')} />;
      default:
        return <TaskListScreen tasks={tasks} onSelectTask={handleSelectTask} onGoToProfile={() => navigateTo('profile')} onGoToWorkPlan={() => navigateTo('workPlan')} onRefresh={fetchTasks} />;
    }
  };

  return (
    <div className="bg-gray-100 font-sans w-full h-full min-h-screen">
      <div className="max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl mx-auto bg-white shadow-lg min-h-screen flex flex-col">
        <main className="flex-grow">
          {renderPage()}
        </main>
        {isLoggedIn && page !== 'taskDetail' && (
          <BottomNav currentPage={page} navigateTo={navigateTo} />
        )}
      </div>
    </div>
  );
}

function LineNoticeScreen({ messageKey }) {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-indigo-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-8 text-center">
          <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Home className="text-indigo-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Homestay Clean</h1>
          <p className="text-gray-600 mt-4 leading-relaxed">{t(messageKey)}</p>
        </div>
      </div>
    </div>
  );
}

function DingTalkNoticeScreen({ message }) {
  return (
    <div className="min-h-screen bg-indigo-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-8 text-center">
          <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Home className="text-indigo-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Homestay Clean</h1>
          <p className="text-gray-600 mt-4 leading-relaxed">{message}</p>
        </div>
      </div>
    </div>
  );
}

function DingTalkConfirmScreen({ name, onConfirm }) {
  return (
    <div className="min-h-screen bg-indigo-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-8 text-center">
          <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="text-indigo-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">确认钉钉姓名</h1>
          <p className="text-gray-500 mt-3">系统检测到你的钉钉姓名为</p>
          <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-xl font-bold text-gray-900">
            {name}
          </div>
          <p className="text-gray-500 mt-4 text-sm leading-relaxed">
            确认后将按这个姓名匹配现有员工；没有匹配到时会提交新账号申请。
          </p>
          <button
            className="mt-6 w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
            type="button"
            onClick={onConfirm}
          >
            确认并进入
          </button>
        </div>
      </div>
    </div>
  );
}

function LineRegistrationScreen({ lineIdToken, onRegistered }) {
  const { t, switchLanguage, language } = useLanguage();
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setMessageType('');

    if (!lineIdToken) {
      setMessage(t('lineRegistration.missingLineInfo'));
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const res = await registerLineUser({
        line_id_token: lineIdToken,
        name,
      });

      setMessage(res.data?.status === 'pending_activation' ? t('lineNotice.pendingRegistration') : t('lineRegistration.success'));
      setMessageType('success');
      onRegistered(res.data);
    } catch (error) {
      const status = error.response?.status;
      const messageKey = status === 422
        ? 'lineRegistration.invalidName'
        : status === 409
          ? 'lineRegistration.conflict'
          : 'lineRegistration.failed';
      setMessage(t(messageKey));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-indigo-600 flex items-center justify-center p-4 md:p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md md:max-w-lg overflow-hidden">
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="text-indigo-600" size={32} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{t('lineRegistration.title')}</h1>
            <p className="text-gray-500 mt-2">{t('lineRegistration.subtitle')}</p>

            <div className="flex justify-center gap-4 mt-4">
              <button
                type="button"
                onClick={() => switchLanguage('zh')}
                className={`text-sm font-medium ${language === 'zh' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
              >
                中文
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() => switchLanguage('ja')}
                className={`text-sm font-medium ${language === 'ja' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
              >
                日本語
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('lineRegistration.nameLabel')}</label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                placeholder={t('lineRegistration.namePlaceholder')}
                required
              />
            </div>

            {message && (
              <p className={`text-sm text-center ${messageType === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <div className="flex items-center">
                  <Loader2 className="animate-spin mr-2" size={18} />
                  {t('lineRegistration.submitting')}
                </div>
              ) : (
                t('lineRegistration.submitButton')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// --- 页面组件 ---

function LoginScreen({ onLogin, onGoToRegister }) {
  const { t, switchLanguage, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [inputCaptcha, setInputCaptcha] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(result);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t('login.errorMissing'));
      return;
    }

    // Captcha validation (client-side mock for now, ideally server-side)
    if (inputCaptcha.toUpperCase() !== captcha) {
      setError(t('login.errorCaptcha'));
      generateCaptcha();
      setInputCaptcha('');
      return;
    }

    setLoading(true);
    try {
      await onLogin(email, password);
      setSuccess(true); // Show success message
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setError(t('login.errorBanned'));
      } else {
        setError(t('login.errorLogin'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-indigo-600 flex items-center justify-center p-4 md:p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md md:max-w-lg lg:max-w-xl overflow-hidden">
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Home className="text-indigo-600" size={32} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{t('login.title')}</h1>
            <p className="text-gray-500 mt-2">{t('login.subtitle')}</p>
            {/* <p className="text-gray-500 mt-2">{t('login.testAccount')}a@a.com</p>
            <p className="text-gray-500 mt-2">{t('login.password')}：password</p> */}

            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={() => switchLanguage('zh')}
                className={`text-sm font-medium ${language === 'zh' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
              >
                中文
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => switchLanguage('ja')}
                className={`text-sm font-medium ${language === 'ja' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
              >
                日本語
              </button>
            </div>
          </div>

          {success ? (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    {t('login.success')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('login.emailLabel')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                    placeholder={t('login.emailPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('login.passwordLabel')}</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                    placeholder={t('login.passwordPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('login.captchaLabel')}</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={inputCaptcha}
                    onChange={(e) => setInputCaptcha(e.target.value)}
                    className="block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                    placeholder={t('login.captchaPlaceholder')}
                    required
                  />
                  <div
                    onClick={generateCaptcha}
                    className="flex items-center justify-center bg-gray-200 text-gray-600 font-mono font-bold text-lg px-4 rounded-lg cursor-pointer select-none tracking-widest w-32"
                    title="点击刷新"
                  >
                    {captcha}
                  </div>
                </div>
              </div>

              {error && <p className="text-red-500 text-sm text-center">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors mt-6 ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <Loader2 className="animate-spin mr-2" size={18} />
                    {t('login.loggingIn')}
                  </div>
                ) : (
                  t('login.loginButton')
                )}
              </button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={onGoToRegister}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-semibold"
                >
                  {t('login.registerLink')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskListScreen({ tasks, onSelectTask, onGoToProfile, onGoToWorkPlan, onRefresh }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('today');
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const todayTasks = tasks.filter(t => t.status !== 20 && t.status !== '20')
    .sort((a, b) => new Date(a.check_out_date) - new Date(b.check_out_date));
  const historyTasks = tasks.filter(t => t.status === 20 || t.status === '20')
    .sort((a, b) => new Date(b.check_out_date) - new Date(a.check_out_date));

  return (
    <div className="p-4 md:p-6 pb-20 md:pb-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{t('tasks.title')}</h1>
          <button
            type="button"
            onClick={onGoToWorkPlan}
            className="inline-flex items-center rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-indigo-700 transition-colors"
          >
            <CalendarDays size={15} className="mr-1.5" />
            {t('workPlan.entryButton')}
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:text-indigo-600 transition-colors disabled:opacity-50"
            title="刷新任务列表"
          >
            <RefreshCw size={24} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button onClick={onGoToProfile} className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:text-indigo-600 transition-colors">
            <User size={24} />
          </button>
        </div>
      </div>
      <div className="mt-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('today')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'today' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            {t('tasks.today')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            {t('tasks.history')}
          </button>
        </nav>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(activeTab === 'today' ? todayTasks : historyTasks).map(task => (
          <TaskCard key={task.id} task={task} onClick={() => {
            if (task.check_out_date) {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const checkOutDate = new Date(task.check_out_date);
              checkOutDate.setHours(0, 0, 0, 0);
              if (checkOutDate > today) {
                if (!window.confirm(`${task.room_name || task.property} 退房时间为 ${task.check_out_date}，尚未到退房时间，是否继续？`)) {
                  return;
                }
              }
            }
            onSelectTask(task);
          }} />
        ))}
        {activeTab === 'today' && todayTasks.length === 0 && <p className="col-span-full text-center text-gray-500 mt-10">{t('tasks.emptyToday')}</p>}
        {activeTab === 'history' && historyTasks.length === 0 && <p className="col-span-full text-center text-gray-500 mt-10">{t('tasks.emptyHistory')}</p>}
      </div>
    </div>
  );
}

function TaskDetailScreen({ task, onUpdateTask, onSubmit, onBack }) {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const [cleanerRemarks, setCleanerRemarks] = useState(task.cleaner_remarks || '');

  if (!task) return null;

  // Helper to safely access items since API structure might differ slightly or be nested
  const getItems = () => task.rooms ? task.rooms.flatMap(r => r.items || []) : [];

  const allRequiredItems = getItems().filter(i => i.requires_photo);
  // Compatible with old API response where is_mandatory might be missing (treat as true)
  const isMandatory = (item) => item.is_mandatory !== false && item.is_mandatory !== 0 && item.is_mandatory !== '0';

  const allMandatoryItems = getItems().filter(i => isMandatory(i));
  const allMandatoryItemsCompleted = allMandatoryItems.every(i => i.is_done);

  const allMandatoryPhotosUploaded = allMandatoryItems
    .filter(i => i.requires_photo)
    .every(i => i.photo);

  const canSubmit = allMandatoryItemsCompleted && allMandatoryPhotosUploaded;

  const handleToggleDone = async (roomId, itemId) => {
    try {
      await toggleItem(task.id, itemId);
      // Optimistic update or refetch
      const updatedTask = { ...task };
      const room = updatedTask.rooms.find(r => r.id === roomId || r.name === roomId); // Handle ID vs Name matching if needed
      // Assuming room structure matches API response
      if (room) {
        const item = room.items.find(i => i.id === itemId);
        if (item) item.is_done = !item.is_done;
      }
      onUpdateTask(updatedTask);
    } catch (error) {
      console.error("Failed to toggle item", error);
    }
  };

  const handleUploadPhoto = async (roomId, itemId, file) => {
    if (!file) return;

    // 客户端验证
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      alert(t('taskDetail.imageTooLarge'));
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('taskDetail.invalidType'));
      return;
    }

    // Dynamic Compression options
    let options = {
      useWebWorker: true,
      fileType: 'image/jpeg'
    };

    const sizeMB = file.size / 1024 / 1024;

    if (sizeMB > 10) {
      // Super large (>10MB): Allow 4K, limit to 3MB
      options.maxSizeMB = 3;
      options.maxWidthOrHeight = 3840;
    } else if (sizeMB > 5) {
      // Large (5-10MB): Allow 2K, limit to 2.5MB
      options.maxSizeMB = 2.5;
      options.maxWidthOrHeight = 2560;
    } else if (sizeMB > 1) {
      // Medium (1-5MB): Allow 1080P, limit to 1.5MB
      options.maxSizeMB = 1.5;
      options.maxWidthOrHeight = 1920;
    } else {
      // Small (<1MB): Keep quality
      options.maxSizeMB = 1;
      options.maxWidthOrHeight = 1920;
    }

    let uploadFile = file;

    try {
      uploadFile = await imageCompression(file, options);
    } catch (error) {
      console.error("Image compression failed, falling back to original file:", error);
      // Fallback to original file
    }

    const formData = new FormData();
    formData.append('photo', uploadFile);

    try {
      const res = await uploadPhoto(task.id, itemId, formData);
      const updatedTask = { ...task };
      const room = updatedTask.rooms.find(r => r.id === roomId || r.name === roomId);
      if (room) {
        const item = room.items.find(i => i.id === itemId);
        if (item) {
          item.photo = res.data.url;
          item.is_done = true;
        }
      }
      onUpdateTask(updatedTask);
    } catch (error) {
      console.error("Failed to upload photo", error);

      // 显示详细的错误信息
      let errorMessage = t('taskDetail.uploadFailed');
      if (error.response) {
        if (error.response.status === 422) {
          errorMessage = t('taskDetail.validationFailed');
          if (error.response.data.errors) {
            console.error('Validation errors:', error.response.data.errors);
          }
        } else if (error.response.status === 404) {
          errorMessage = t('taskDetail.itemNotFound');
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = t('taskDetail.networkError');
      }

      alert(errorMessage);
    }
  };

  const handleDeletePhoto = async (roomId, itemId) => {
    try {
      await deletePhoto(task.id, itemId);
      const updatedTask = { ...task };
      const room = updatedTask.rooms.find(r => r.id === roomId || r.name === roomId);
      if (room) {
        const item = room.items.find(i => i.id === itemId);
        if (item) {
          item.photo = null;
          item.is_done = false;
        }
      }
      onUpdateTask(updatedTask);
    } catch (error) {
      console.error("Failed to delete photo", error);
    }
  };

  const handleSubmitClick = () => {
    if (canSubmit) {
      setIsSubmitting(true);
      setTimeout(() => {
        onSubmit(task, cleanerRemarks);
        setIsSubmitting(false);
      }, 1500);
    } else {
      setShowSubmitModal(true);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <header className="sticky top-0 bg-white shadow-sm z-10 p-4 md:p-6 flex items-center">
        <button onClick={onBack} className="p-2 -ml-2 mr-2 text-gray-600"><ArrowLeft size={24} /></button>
        <h1 className="text-lg md:text-xl font-bold text-gray-800 truncate">
          {task.status === 20 || task.status === '20' ? t('taskDetail.historyTitle') : t('taskDetail.title')}
        </h1>
      </header>

      <div className="flex-grow p-4 md:p-6 pb-24 md:pb-6">
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md mb-4 md:grid md:grid-cols-2 md:gap-4">
          <h2 className="font-bold text-xl md:text-2xl text-gray-800">{task.room_name || task.property}</h2>

          <p className="text-sm text-gray-500 mt-1 md:col-span-2">
            清扫时间：{task.scheduled_time ? task.scheduled_time.split(' ')[0] : ''}
          </p>

          <p className="text-sm text-gray-500 mt-1 md:col-span-2">
            {task.check_in_date && '下次入住：' + task.check_in_date}
          </p>

          <p className="text-sm text-gray-500 mt-1 md:col-span-2">
            成人：{task.numAdult || 0}  儿童：{task.numChild || 0}
          </p>
          {task.remarks && (
            <p className="text-base text-red-600 font-bold mt-2 md:col-span-2 border-t pt-2 border-gray-100">
              {t('taskDetail.remarks') || '备注'}：{task.remarks}
            </p>
          )}
        </div>

        {task.rooms.map(room => {
          const areas = [];
          const itemsByArea = {};

          room.items.forEach(item => {
            const area = item.area_name || t('taskDetail.otherArea');
            if (!itemsByArea[area]) {
              itemsByArea[area] = [];
              areas.push(area);
            }
            itemsByArea[area].push(item);
          });

          // Determine if task is read-only (history)
          const isReadOnly = task.status === 20 || task.status === '20';

          return (
            <div key={room.name} className="mb-6">
              {console.log(room)}
              <h3 className="text-lg font-semibold text-indigo-700 mb-3 border-l-4 border-indigo-500 pl-3">清扫项目</h3>
              {areas.map(areaName => (
                <div key={areaName} className="mb-4">
                  <h4 className="font-medium text-gray-600 mb-2 ml-1 bg-gray-100 inline-block px-2 py-1 rounded text-sm">{areaName}</h4>
                  <div className="space-y-3 md:space-y-4">
                    {itemsByArea[areaName].map(item => (
                      <ChecklistItem
                        key={item.id}
                        item={item}
                        readOnly={isReadOnly}
                        onToggleDone={() => handleToggleDone(room.id, item.id)}
                        onUploadPhoto={(file) => handleUploadPhoto(room.id, item.id, file)}
                        onDeletePhoto={() => handleDeletePhoto(room.id, item.id)}
                        onImageClick={(img) => setModalImage(img)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('taskDetail.cleanerRemarks') || '清扫备注'}</label>
          <textarea
            value={cleanerRemarks}
            onChange={(e) => setCleanerRemarks(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            rows="3"
            disabled={task.status === 20 || task.status === '20'}
          />
        </div>
      </div>

      {task.status !== 20 && task.status !== '20' && (
        <footer className="fixed bottom-0 left-0 right-0 max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl mx-auto p-4 md:p-6 bg-white border-t border-gray-200">
          <button
            onClick={handleSubmitClick}
            disabled={isSubmitting}
            className={`w-full font-bold py-3 px-4 rounded-lg shadow-lg transition-all duration-300 flex items-center justify-center ${canSubmit
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              } ${isSubmitting ? 'bg-indigo-400' : ''}`}
          >
            {isSubmitting ? <><Loader2 className="animate-spin mr-2" size={20} />{t('taskDetail.submitting')}</> : t('taskDetail.submit')}
          </button>
        </footer>
      )}

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 m-4 text-center shadow-xl">
            <XCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h3 className="text-lg font-bold">{t('taskDetail.cantSubmitTitle')}</h3>
            <p className="text-gray-600 mt-2 text-sm">{t('taskDetail.cantSubmitMsg')}</p>
            <button onClick={() => setShowSubmitModal(false)} className="mt-6 bg-indigo-600 text-white px-6 py-2 rounded-lg w-full">{t('taskDetail.ok')}</button>
          </div>
        </div>
      )}
      {modalImage && <ImageModal src={modalImage} onClose={() => setModalImage(null)} />}
    </div>
  );
}



function ProfileScreen({ user, isLineSession, isDingTalkSession, onGoToTasks, onLogout }) {
  const { t, switchLanguage, language } = useLanguage();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const canUseAccountActions = !isLineSession && !isDingTalkSession;

  return (
    <div className="p-4 md:p-6 pb-20 md:pb-6">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">{t('profile.title')}</h1>
      <div className="flex items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="ml-4">
          <p className="font-bold text-xl text-gray-800">{user.name}</p>
        </div>
      </div>

      <div className="mt-4 flex justify-center gap-4">
        <button
          onClick={() => switchLanguage('zh')}
          className={`text-sm font-medium ${language === 'zh' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
        >
          中文
        </button>
        <span className="text-gray-300">|</span>
        <button
          onClick={() => switchLanguage('ja')}
          className={`text-sm font-medium ${language === 'ja' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
        >
          日本語
        </button>
      </div>

      <div className="mt-8 space-y-3">
        <button onClick={onGoToTasks} className="w-full bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-indigo-700 transition-colors flex items-center justify-center">
          <RefreshCw size={16} className="mr-2" />
          {t('profile.backToTasks')}
        </button>

        {canUseAccountActions && (
          <button onClick={() => setShowChangePassword(true)} className="w-full bg-white text-indigo-600 font-bold py-3 px-4 rounded-lg shadow-sm border border-indigo-100 hover:bg-indigo-50 transition-colors flex items-center justify-center">
            <Lock size={16} className="mr-2" />
            {t('profile.changePassword')}
          </button>
        )}

        {canUseAccountActions && (
          <button onClick={onLogout} className="w-full bg-white text-red-500 font-bold py-3 px-4 rounded-lg shadow-sm border border-red-100 hover:bg-red-50 transition-colors flex items-center justify-center">
            <LogOut size={16} className="mr-2" />
            {t('profile.logout')}
          </button>
        )}
      </div>

      {canUseAccountActions && showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const { t } = useLanguage();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError(t('changePassword.errorMissing'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('changePassword.errorMismatch'));
      return;
    }

    try {
      await changePassword({ old_password: oldPassword, new_password: newPassword, new_password_confirmation: confirmPassword });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.message || t('changePassword.errorFailed'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 m-4 w-full max-w-sm shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">{t('changePassword.title')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle size={24} />
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="mx-auto text-green-500 mb-3" size={48} />
            <p className="text-green-600 font-bold">{t('changePassword.success')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('changePassword.oldPassword')}</label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                placeholder={t('changePassword.oldPasswordPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('changePassword.newPassword')}</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                placeholder={t('changePassword.newPasswordPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('changePassword.confirmPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                placeholder={t('changePassword.confirmPasswordPlaceholder')}
              />
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              type="submit"
              className="w-full bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-indigo-700 transition-colors mt-2"
            >
              {t('changePassword.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// --- 可复用组件 ---

function TaskCard({ task, onClick }) {
  const { t } = useLanguage();
  const statusStyles = {
    0: 'bg-blue-100 text-blue-800', 10: 'bg-yellow-100 text-yellow-800',
    20: 'bg-green-100 text-green-800',
    '待打扫': 'bg-blue-100 text-blue-800', '进行中': 'bg-yellow-100 text-yellow-800',
    '待审核': 'bg-purple-100 text-purple-800', '已完成': 'bg-green-100 text-green-800',
  };

  const getStatusText = (status) => {
    if (status === 0 || status === '0') return t('status.pending');
    if (status === 10 || status === '10') return t('status.inProgress');
    if (status === 20 || status === '20') return t('status.completed');
    if (status === '待打扫') return t('status.pending');
    if (status === '进行中') return t('status.inProgress');
    if (status === '待审核') return t('status.reviewing');
    if (status === '已完成') return t('status.completed');
    return status;
  };

  return (
    <div onClick={onClick} className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border-l-4 h-full" style={{ borderColor: '#6366f1' }}>
      <div className="flex justify-between items-start">
        <h3 className="font-bold text-gray-800 pr-2 flex-1">{task.room_name || task.property}</h3>
        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusStyles[task.status] || 'bg-gray-100 text-gray-800'}`}>{getStatusText(task.status)}</span>
      </div>

      <p className="text-sm text-gray-500 mt-1">
        {t('tasks.checkOutDate') || '退房时间'}：{task.check_out_date}
      </p>
      <p className="text-sm text-gray-500 mt-1">
        {task.check_in_date && '下次入住：' + task.check_in_date}
      </p>
      {task.remarks && (
        <p className="text-sm text-red-600 font-bold mt-1">
          {t('taskDetail.remarks') || '备注'}：{task.remarks}
        </p>
      )}
    </div>
  );
}

function ChecklistItem({ item, readOnly, onToggleDone, onUploadPhoto, onDeletePhoto, onImageClick }) {
  const { t } = useLanguage();

  const getImageUrl = (path) => {
    if (!path) return `${ASSET_BASE_URL}/404.jpg`;
    if (path.startsWith('http') || path.startsWith('blob:')) return path;

    // Remove leading slash if present to normalize
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    // If it's already a storage path, just append to server URL
    if (cleanPath.startsWith('storage/')) {
      return `${ASSET_BASE_URL}/${cleanPath}`;
    }

    // Otherwise assume it needs to be in storage
    return `${ASSET_BASE_URL}/storage/${cleanPath}`;
  };

  return (
    <div className={`bg-white p-3 rounded-lg shadow-sm transition-all ${item.is_done ? 'bg-green-50' : ''}`}>
      <div
        className={`flex items-start ${!readOnly ? 'cursor-pointer' : ''}`}
        onClick={readOnly ? null : onToggleDone}
      >
        <div
          className={`mt-1 mr-3 flex-shrink-0 ${readOnly ? 'opacity-60' : ''}`}
        >
          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${item.is_done ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
            {item.is_done == 0 ? '' : <Check size={16} className="text-white" />}
          </div>
        </div>
        <div>
          <p className={`font-semibold ${item.is_done ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
            {item.text}
            {/* Show asterisk if item is mandatory AND requires photo (or just mandatory?) usually * implies required field. */
              /* Based on user request, "Guest Lost Items" is non-mandatory, so it shouldn't have *. */
              (item.requires_photo && (item.is_mandatory !== false && item.is_mandatory !== 0 && item.is_mandatory !== '0')) ? <span className="text-red-500 ml-1">*</span> : ""}
          </p>
          <p className="text-xl text-gray-400 mt-1">{item.standard}</p>
        </div>
      </div>

      {(item.requires_photo == 0 ? '' : item.requires_photo) && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-center text-xs font-semibold text-gray-500 mb-1">{t('taskDetail.standardExample')}</label>
            <div className="relative w-full aspect-square bg-gray-100 rounded-md overflow-hidden cursor-pointer" onClick={() => onImageClick(getImageUrl(item.standard_photo))}>

              <img src={getImageUrl(item.standard_photo)} alt={t('taskDetail.standardExample')} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 flex items-center justify-center transition-opacity">
                <ZoomIn size={24} className="text-white opacity-0 hover:opacity-100" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-center text-xs font-semibold text-gray-500 mb-1">{t('taskDetail.myPhoto')}</label>
            {item.photo ? (
              <div className="relative w-full aspect-square bg-gray-100 rounded-md overflow-hidden group">
                <img src={getImageUrl(item.photo)} alt={t('taskDetail.myPhoto')} className="w-full h-full object-cover cursor-pointer" onClick={() => onImageClick(getImageUrl(item.photo))} />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-opacity cursor-pointer" onClick={() => onImageClick(getImageUrl(item.photo))}>
                  <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-100" />
                </div>
                {!readOnly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePhoto();
                    }}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg"
                    title={t('common.deletePhoto')}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ) : (
              !readOnly && (
                <label className="w-full aspect-square bg-gray-100 md:bg-white md:border md:border-indigo-200 rounded-md flex flex-col items-center justify-center text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer">
                  <Camera size={24} />
                  <span className="text-xs mt-1 font-semibold">{t('taskDetail.clickUpload')}</span>
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => onUploadPhoto(e.target.files[0])} />
                </label>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BottomNav({ currentPage, navigateTo }) {
  const { t } = useLanguage();
  const navItems = [
    { name: 'tasks', label: t('nav.tasks'), icon: Home },
    { name: 'inventory', label: t('nav.inventory'), icon: Package },
  ];

  return (
    <footer className="fixed bottom-0 left-0 right-0 max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl mx-auto bg-white border-t md:border-b border-gray-200 md:static md:max-w-none md:w-full md:shadow-sm">
      <div className="flex justify-around">
        {navItems.map(item => (
          <button
            key={item.name}
            onClick={() => navigateTo(item.name)}
            className={`flex flex-col items-center justify-center w-full py-2 ${currentPage === item.name ? 'text-indigo-600' : 'text-gray-400'}`}
          >
            <item.icon size={24} />
            <span className="text-xs mt-1">{item.label}</span>
          </button>
        ))}
      </div>
    </footer>
  );
}

function ImageModal({ src, onClose }) {
  const { t } = useLanguage();
  if (!src) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div className="relative max-w-full max-h-full p-4" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={t('common.viewImage')} className="max-w-full max-h-[80vh] rounded-lg shadow-2xl" />
        <button onClick={onClose} className="absolute -top-2 -right-2 bg-white rounded-full p-1.5 text-gray-800 shadow-lg">
          <XCircle size={28} />
        </button>
      </div>
    </div>
  );
}
